/**
 * The two uploads. They are the same MECHANISM and opposite PROMISES, and the difference has to
 * survive all the way into the wording.
 *
 * THE MECHANISM, both times: the browser mints an `intakeId` (8–64 characters, and it IS the
 * idempotency key — claiming the same object twice returns the same stored file), writes the bytes
 * straight into its own Firebase Storage quarantine prefix, and only then calls the claim
 * operation to name that object. Nothing is ever POSTed to us. The prefix is keyed on the caller's
 * uid, which comes from the token, so an intakeId cannot reach another author's uploads.
 *
 * THE PROMISES, which are opposite:
 *   AVATAR — required to submit, BECOMES PUBLIC, and replacing it destroys BOTH previous copies in
 *   the same transaction. One author, one avatar, always. Validated by MAGIC BYTES and not by
 *   extension; JPEG, PNG or WebP only, and SVG is rejected.
 *   IDENTITY EVIDENCE — optional (5 weight), stored ONLY under the private prefix with no
 *   rendition and no public object ever, read once by a reviewer, CANNOT BE WITHDRAWN by the
 *   author, and destroyed 90 days after the DECISION — the clock starts at the decision and not at
 *   the upload, so an applicant who waits three weeks does not lose their evidence mid-review.
 */
import { useCallback, useState } from 'react';
import { FileTooLarge } from '../api/problem';
import type { Credential, StoredImage } from '../contract';
import { LIMITS, MAX_UPLOAD_BYTES } from '../domain/limits';
import { refreshAccountPhoto } from '../../shell/account-photo';
import { useApplicationApi, useQuarantineUploader } from './provider';

export type UploadPhase = 'idle' | 'writing' | 'claiming' | 'done' | 'failed';

export interface UploadState<T> {
  readonly phase: UploadPhase;
  /** 0…1 while writing to Storage. The claim itself has no progress to report. */
  readonly progress: number;
  readonly error: unknown;
  readonly result: T | undefined;
  readonly reset: () => void;
}

/**
 * Mints the intake id. `crypto.randomUUID()` is 36 characters, comfortably inside the 8–64 bound,
 * and needs no dependency. It is the idempotency key, so it is minted once per file and not once
 * per attempt: retrying a failed claim with the SAME id is what makes the retry safe.
 */
function mintIntakeId(): string {
  const id = crypto.randomUUID();
  return id.length >= LIMITS.intakeId.min ? id : `intake-${id}`;
}

/**
 * ★ THE SIZE IS REFUSED HERE, BEFORE A BYTE MOVES, and before an intake id is minted. At or over
 * `MAX_UPLOAD_BYTES` the quarantine rule refuses the write (../domain/limits.ts cites both rules
 * and why it is `>=`), and in production it refuses with a bare 403 before any claim - which a
 * person can only read as "something went wrong". Refused here, the refusal says why, and nothing
 * half-written is left in the prefix.
 */
function refusedBeforeWriting(file: File): FileTooLarge | null {
  return file.size >= MAX_UPLOAD_BYTES ? new FileTooLarge(file.size) : null;
}

export interface AvatarUpload extends UploadState<StoredImage> {
  readonly upload: (file: File) => Promise<StoredImage | undefined>;
}

export function useAvatarUpload(onClaimed: (image: StoredImage) => void): AvatarUpload {
  const api = useApplicationApi();
  const uploader = useQuarantineUploader();
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<StoredImage | undefined>(undefined);

  const upload = useCallback(
    async (file: File): Promise<StoredImage | undefined> => {
      const refused = refusedBeforeWriting(file);
      if (refused !== null) {
        setError(refused);
        setProgress(0);
        setPhase('failed');
        return undefined;
      }
      const intakeId = mintIntakeId();
      setError(null);
      setProgress(0);
      setPhase('writing');
      try {
        await uploader.putQuarantineObject(intakeId, file, setProgress);
        setPhase('claiming');
        const image = await api.claimAvatarUpload({ intakeId, originalName: file.name });
        setResult(image);
        setPhase('done');
        // The shell's photo (design 24, T13) is read once per load; a new one is shown at once.
        refreshAccountPhoto();
        onClaimed(image);
        return image;
      } catch (cause) {
        setError(cause);
        setPhase('failed');
        return undefined;
      }
    },
    [api, uploader, onClaimed],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(0);
    setError(null);
  }, []);

  return { phase, progress, error, result, upload, reset };
}

export interface CredentialUpload extends UploadState<Credential> {
  readonly upload: (file: File) => Promise<Credential | undefined>;
}

export function useCredentialUpload(onClaimed: (credential: Credential) => void): CredentialUpload {
  const api = useApplicationApi();
  const uploader = useQuarantineUploader();
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<Credential | undefined>(undefined);

  const upload = useCallback(
    async (file: File): Promise<Credential | undefined> => {
      const refused = refusedBeforeWriting(file);
      if (refused !== null) {
        setError(refused);
        setProgress(0);
        setPhase('failed');
        return undefined;
      }
      const intakeId = mintIntakeId();
      setError(null);
      setProgress(0);
      setPhase('writing');
      try {
        await uploader.putQuarantineObject(intakeId, file, setProgress);
        setPhase('claiming');
        // `fileName` is required here and is what the author sees in their own list afterwards —
        // there is never an object URL to show them instead.
        const record = await api.claimCredentialUpload({
          intakeId,
          fileName: file.name.slice(0, LIMITS.credentialFileName.max),
        });
        setResult(record);
        setPhase('done');
        onClaimed(record);
        return record;
      } catch (cause) {
        setError(cause);
        setPhase('failed');
        return undefined;
      }
    },
    [api, uploader, onClaimed],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(0);
    setError(null);
  }, []);

  return { phase, progress, error, result, upload, reset };
}
